import { useState, useMemo, useEffect, useCallback } from 'react'
import { Fade, IconButton, Alert } from '@mui/material'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import {
  LfCloseIcon,
  CloseRoundFullIcon,
  InfoFullIcon,
  CheckFillIcon,
  WarningFillIcon
} from 'src/components/shared/ui/Icons'
import { getInfoIconColor, infoBarStyles as styles } from 'src/components/shared/ui/InfoBar/InfoBarStyles'
import { theme } from 'src/providers/ThemeProvider'

type Props = {
  message: string | JSX.Element
  type?: AlertType
  showCloseIcon?: boolean
  customAction?: () => void
  autoClose?: boolean
  iconVariant?: 'status' | 'info'
}

export default function InfoBar({
  message,
  type = AlertType.INFO,
  showCloseIcon = true,
  customAction,
  autoClose = false,
  iconVariant = 'status'
}: Props): JSX.Element {
  const [open, setOpen] = useState(true)

  const closeAlert = useCallback(() => {
    setOpen(false)
    if (customAction) {
      setTimeout(() => {
        customAction()
      }, 1500)
    }
  }, [customAction])

  function renderIconType(type: string): JSX.Element {
    if (iconVariant === 'info') {
      return <InfoFullIcon htmlColor={getInfoIconColor(type)} sx={{ fontSize: '22px' }} />
    }
    switch (type) {
      case AlertType.SUCCESS:
        return <CheckFillIcon htmlColor={theme.colors.seaGreen200} sx={{ fontSize: '22px' }} />
      case AlertType.ERROR:
        return <CloseRoundFullIcon htmlColor={theme.colors.red01} sx={{ fontSize: '22px' }} />
      case AlertType.WARNING:
        return <WarningFillIcon htmlColor={theme.colors.saffron200} sx={{ fontSize: '22px' }} />
      case AlertType.INFO:
        return <InfoFullIcon htmlColor={theme.colors.byzantineBlue200} sx={{ fontSize: '22px' }} />
      default:
        return <></>
    }
  }

  const renderCloseIcon = useMemo((): JSX.Element => {
    if (showCloseIcon) {
      return (
        <IconButton
          sx={styles(type).closeButton}
          aria-label="close"
          size="small"
          onClick={closeAlert}
        >
          <LfCloseIcon fontSize="inherit" />
        </IconButton>
      )
    }
    return <></>
  }, [showCloseIcon, type, closeAlert])

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        closeAlert()
      }, 3000)
    }
  }, [autoClose, message, closeAlert])

  return (
    <Fade in={open} unmountOnExit timeout={750}>
      <Alert icon={renderIconType(type)} action={renderCloseIcon} sx={styles(type).container}>
        {message}
      </Alert>
    </Fade>
  )
}
